import { useMemo, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FolderKanban,
  Layers3,
  MonitorSmartphone,
  Rocket,
  ServerCog,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'

const PRIORITIES = [
  { id: 'web', label: 'Web-first', icon: MonitorSmartphone },
  { id: 'mobile', label: 'Mobile-first', icon: Rocket },
  { id: 'desktop', label: 'Desktop', icon: FolderKanban },
]

const STACKS = ['MERN', 'Next.js + Supabase', 'Django + React', 'FastAPI + React']
const AUTH_STRATEGIES = ['Manual (JWT)', 'Google OAuth + JWT', 'Supabase Auth']
const AI_AGENTS = [
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' },
]

const INITIAL_FORM = {
  projectName: '',
  requirement: '',
  aiAgent: AI_AGENTS[1].id,
  stack: STACKS[1],
  priority: PRIORITIES[0].id,
  auth: AUTH_STRATEGIES[0],
  visibility: 'public',
  weeklyHours: '15-20',
}

function Progress({ step }) {
  const percent = Math.round((step / 4) * 100)
  return (
    <div className="np-progress-wrap">
      <div className="np-progress-copy">
        <span>Step {step} of 4</span>
        <span>{percent}% Complete</span>
      </div>
      <div className="np-progress-track">
        <div className="np-progress-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

function StepRequirement({
  form,
  setForm,
  requirementLoading,
  requirementDoc,
  requirementError,
  onGenerateDocumentation,
}) {
  return (
    <section className="np-step">
      <h2>What are we building today?</h2>
      <p>Describe your goals and I will shape a practical system blueprint and setup plan.</p>
      <label className="np-field">
        <span>Project Name</span>
        <input
          value={form.projectName}
          onChange={e => setForm(prev => ({ ...prev, projectName: e.target.value }))}
          placeholder="arkitect-ai-dashboard"
          autoFocus
        />
      </label>
      <label className="np-field">
        <div className="np-field-head">
          <span>Requirement Input</span>
          <button
            type="button"
            className="np-inline-btn"
            disabled={requirementLoading || !form.requirement.trim()}
            onClick={onGenerateDocumentation}
          >
            {requirementLoading ? 'Generating...' : 'Generate Documentation'}
          </button>
        </div>
        <textarea
          rows={5}
          value={form.requirement}
          onChange={e => setForm(prev => ({ ...prev, requirement: e.target.value }))}
          placeholder="Build a high-performance fintech dashboard..."
        />
      </label>
      <label className="np-field">
        <span>AI Agent</span>
        <div className="np-agent-pills">
          {AI_AGENTS.map(agent => (
            <button
              key={agent.id}
              type="button"
              className={`np-agent-pill ${form.aiAgent === agent.id ? 'active' : ''}`}
              onClick={() => setForm(prev => ({ ...prev, aiAgent: agent.id }))}
            >
              {agent.label}
            </button>
          ))}
        </div>
      </label>
      {requirementError ? <div className="np-error">{requirementError}</div> : null}
      {requirementDoc ? (
        <div className="np-requirement-ai">
          <div className="np-requirement-ai-head">
            <strong>Generated Documentation</strong>
            <button
              type="button"
              className="np-inline-btn"
              onClick={() => setForm(prev => ({ ...prev, requirement: requirementDoc }))}
            >
              Use this
            </button>
          </div>
          <p>{requirementDoc}</p>
        </div>
      ) : null}
      <div className="np-mini-note">
        <Sparkles size={14} />
        <span>{AI_AGENTS.find(agent => agent.id === form.aiAgent)?.label || 'AI'} suggestions will tune stack and architecture for this requirement.</span>
      </div>
    </section>
  )
}

function StepScope({ form, setForm }) {
  return (
    <section className="np-step">
      <h2>Refining your project scope</h2>
      <p>Pick core constraints so generated architecture is production-ready from day one.</p>
      <label className="np-field">
        <span>Preferred Stack</span>
        <select value={form.stack} onChange={e => setForm(prev => ({ ...prev, stack: e.target.value }))}>
          {STACKS.map(stack => <option key={stack} value={stack}>{stack}</option>)}
        </select>
      </label>
      <div className="np-priority-grid">
        {PRIORITIES.map(item => {
          const Icon = item.icon
          const active = form.priority === item.id
          return (
            <button
              key={item.id}
              type="button"
              className={`np-priority-card ${active ? 'active' : ''}`}
              onClick={() => setForm(prev => ({ ...prev, priority: item.id }))}
            >
              <Icon size={16} />
              <span>{item.label}</span>
            </button>
          )
        })}
      </div>
      <label className="np-field">
        <span>Authentication Strategy</span>
        <select value={form.auth} onChange={e => setForm(prev => ({ ...prev, auth: e.target.value }))}>
          {AUTH_STRATEGIES.map(auth => <option key={auth} value={auth}>{auth}</option>)}
        </select>
      </label>
    </section>
  )
}

function StepBlueprint({ form, setForm, blueprintLoading, blueprintText, blueprintError }) {
  const modules = useMemo(() => {
    return [
      { title: 'Core Frontend', detail: `${form.stack} UI scaffold with role-aware routing`, icon: Layers3 },
      { title: 'Backend Services', detail: `API contracts tuned for ${form.priority} delivery`, icon: ServerCog },
      { title: 'Security', detail: `${form.auth} + baseline policy and session checks`, icon: ShieldCheck },
    ]
  }, [form.auth, form.priority, form.stack])

  return (
    <section className="np-step">
      <h2>Final System Blueprint</h2>
      <p>Review generated modules before we create and register your project.</p>
      {blueprintLoading ? (
        <div className="np-blueprint-ai">
          <strong>Generating {form.aiAgent} blueprint...</strong>
          <span>Analyzing requirements and preparing architecture recommendations.</span>
        </div>
      ) : null}
      {!blueprintLoading && blueprintText ? (
        <div className="np-blueprint-ai">
          <strong>{form.aiAgent} recommendations</strong>
          <p>{blueprintText}</p>
        </div>
      ) : null}
      {blueprintError ? <div className="np-error">{blueprintError}</div> : null}
      <div className="np-blueprint-list">
        {modules.map(module => {
          const Icon = module.icon
          return (
            <div key={module.title} className="np-blueprint-card">
              <Icon size={16} />
              <div>
                <strong>{module.title}</strong>
                <span>{module.detail}</span>
              </div>
            </div>
          )
        })}
      </div>
      <div className="np-row">
        <label className="np-field">
          <span>Visibility</span>
          <select value={form.visibility} onChange={e => setForm(prev => ({ ...prev, visibility: e.target.value }))}>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </label>
        <label className="np-field">
          <span>Commitment / week</span>
          <input
            value={form.weeklyHours}
            onChange={e => setForm(prev => ({ ...prev, weeklyHours: e.target.value }))}
            placeholder="10-15"
          />
        </label>
      </div>
    </section>
  )
}

function StepLaunch({ form }) {
  return (
    <section className="np-step">
      <h2>System Plan Of Action</h2>
      <p>Your project is ready to initialize in the local workspace and sync with Arkitect IDE services.</p>
      <div className="np-launch-grid">
        <div><span>Project</span><strong>{form.projectName || 'Untitled Project'}</strong></div>
        <div><span>AI Agent</span><strong>{AI_AGENTS.find(agent => agent.id === form.aiAgent)?.label || form.aiAgent}</strong></div>
        <div><span>Stack</span><strong>{form.stack}</strong></div>
        <div><span>Priority</span><strong>{PRIORITIES.find(item => item.id === form.priority)?.label}</strong></div>
        <div><span>Visibility</span><strong>{form.visibility}</strong></div>
      </div>
      <div className="np-ready">
        <CheckCircle2 size={18} />
        <span>Ready to initialize the build?</span>
      </div>
    </section>
  )
}

export function NewProjectFlow({ open, onClose, onCreate, onGenerateBlueprint, creating, error }) {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState(INITIAL_FORM)
  const [requirementLoading, setRequirementLoading] = useState(false)
  const [requirementDoc, setRequirementDoc] = useState('')
  const [requirementError, setRequirementError] = useState('')
  const [blueprintLoading, setBlueprintLoading] = useState(false)
  const [blueprintError, setBlueprintError] = useState('')
  const [blueprintText, setBlueprintText] = useState('')

  if (!open) {
    return null
  }

  const canMoveNext = step !== 1 || form.projectName.trim().length >= 2

  const onGenerateDocumentation = async () => {
    if (!onGenerateBlueprint) {
      return
    }
    try {
      setRequirementLoading(true)
      setRequirementError('')
      const generated = await onGenerateBlueprint({
        projectName: form.projectName.trim(),
        requirement: form.requirement.trim(),
        stack: form.stack,
        priority: form.priority,
        auth: form.auth,
        aiAgent: form.aiAgent,
        mode: 'requirements',
      })
      setRequirementDoc(generated || '')
    } catch (nextError) {
      setRequirementError(String(nextError?.message || 'Unable to generate documentation right now.'))
      setRequirementDoc('')
    } finally {
      setRequirementLoading(false)
    }
  }

  const onNext = async () => {
    if (step === 2 && onGenerateBlueprint) {
      try {
        setBlueprintLoading(true)
        setBlueprintError('')
        const generated = await onGenerateBlueprint({
          projectName: form.projectName.trim(),
          requirement: form.requirement.trim(),
          stack: form.stack,
          priority: form.priority,
          auth: form.auth,
          aiAgent: form.aiAgent,
          mode: 'blueprint',
        })
        setBlueprintText(generated || '')
      } catch (nextError) {
        setBlueprintError(String(nextError?.message || 'Unable to generate AI blueprint right now.'))
        setBlueprintText('')
      } finally {
        setBlueprintLoading(false)
      }
    }
    setStep(value => Math.min(4, value + 1))
  }
  const onBack = () => setStep(value => Math.max(1, value - 1))

  const onSubmit = async () => {
    await onCreate({
      name: form.projectName.trim(),
      visibility: form.visibility,
      requirement: form.requirement.trim(),
      aiAgent: form.aiAgent,
      blueprint: blueprintText,
    })
    setStep(1)
    setForm(INITIAL_FORM)
    setRequirementDoc('')
    setRequirementError('')
    setBlueprintText('')
    setBlueprintError('')
  }

  return (
    <div className="np-overlay" role="dialog" aria-modal="true">
      <div className="np-shell">
        <div className="np-header">
          <div className="np-brand">AI Architect</div>
          <button className="np-ghost" onClick={onClose}>Close</button>
        </div>
        <Progress step={step} />

        {step === 1 && (
          <StepRequirement
            form={form}
            setForm={setForm}
            requirementLoading={requirementLoading}
            requirementDoc={requirementDoc}
            requirementError={requirementError}
            onGenerateDocumentation={onGenerateDocumentation}
          />
        )}
        {step === 2 && <StepScope form={form} setForm={setForm} />}
        {step === 3 && (
          <StepBlueprint
            form={form}
            setForm={setForm}
            blueprintLoading={blueprintLoading}
            blueprintText={blueprintText}
            blueprintError={blueprintError}
          />
        )}
        {step === 4 && <StepLaunch form={form} />}

        {error && <div className="np-error">{error}</div>}

        <div className="np-footer">
          <button className="np-ghost" onClick={step === 1 ? onClose : onBack} disabled={creating}>
            <ArrowLeft size={14} /> {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button className="np-primary" onClick={onNext} disabled={!canMoveNext || creating || blueprintLoading}>
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button className="np-primary" onClick={onSubmit} disabled={creating || form.projectName.trim().length < 2}>
              {creating ? 'Creating...' : 'Accept & Build'} <Rocket size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
